import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  STORY_REACTIONS,
  STORY_REPLY_MAX,
  STORY_REPORT_REASONS,
  type StoryReaction,
  storyReactionEmoji,
} from '@goaltree/backend/convex/lib/stories'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import {
  Ban,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  Flag,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Plus,
  Send,
  ShieldAlert,
  Target,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'
import { type CSSProperties, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ComposerDialog } from '@/components/composer-dialog'
import { habitColor } from '@/components/inbox-habits'
import { UserAvatar } from '@/components/user-avatar'
import { authClient } from '@/lib/auth-client'

export type StoryPerson = FunctionReturnType<typeof api.stories.rail>[number]
type StoryItem = StoryPerson['items'][number]

const LeaderboardUserSheet = lazy(() =>
  import('@/components/leaderboard-user-sheet').then((m) => ({
    default: m.LeaderboardUserSheet,
  })),
)

type StoryActivityPerson = { userId: string; name: string; image: string | null }

const PHOTO_MS = 5000
const VIDEO_MAX_MS = 15_000
const HOLD_MS = 220

function storyAgo(ms: number, locale: string) {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })
  const mins = Math.max(1, Math.round((Date.now() - ms) / 60_000))
  if (mins < 60) return rtf.format(-mins, 'minute')
  return rtf.format(-Math.round(mins / 60), 'hour')
}

/** Shares today's check-in capture to your story, with its own toasts. */
export function useAddToStory() {
  const { t } = useTranslation()
  const add = useMutation(api.stories.add)
  return useCallback(
    (habitId: Id<'nodes'>, date: string) =>
      add({ habitId, date })
        .then(() => toast.success(t('stories.added')))
        .catch(() => toast.error(t('stories.addFailed'))),
    [add, t],
  )
}

export function StoryRing({
  person,
  size,
  onOpen,
}: {
  person: Pick<StoryPerson, 'name' | 'image' | 'allSeen' | 'isMe'>
  size: number
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const label = person.isMe ? t('stories.yourStory') : person.name
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('stories.open', { name: label })}
      className="group flex w-[72px] shrink-0 cursor-pointer flex-col items-center gap-1.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
    >
      <span
        className={cn(
          'grid place-items-center rounded-full p-[2.5px] transition-transform group-active:scale-95',
          person.allSeen ? 'bg-muted-foreground/30' : 'bg-primary',
        )}
      >
        <span className="rounded-full bg-background p-[2px]">
          <UserAvatar
            image={person.image}
            name={person.name}
            size={size}
            className="block rounded-full bg-muted object-cover"
          />
        </span>
      </span>
      <span
        className={cn(
          'w-full truncate text-center text-[11.5px] leading-tight',
          person.allSeen ? 'text-muted-foreground' : 'font-semibold text-foreground',
        )}
      >
        {label}
      </span>
    </button>
  )
}

function AddBadge({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="absolute top-[46px] left-[46px] grid size-6 cursor-pointer place-items-center rounded-full bg-primary text-primary-foreground ring-[3px] ring-background transition-transform hover:scale-105 active:scale-95"
    >
      <Plus className="size-3.5" strokeWidth={3} />
    </button>
  )
}

/**
 * The feed's row of story avatars, led by Your story with its "+". `linkItemId`
 * (a story notification) opens your viewer on that item while it's live, else
 * its activity sheet while it's held.
 */
export function StoryRail({
  linkItemId,
  onLinkHandled,
}: {
  linkItemId?: string
  onLinkHandled?: () => void
} = {}) {
  const { t } = useTranslation()
  const people = useQuery(api.stories.rail)
  const { data: session } = authClient.useSession()
  const [composing, setComposing] = useState(false)
  const [open, setOpen] = useState<{
    people: StoryPerson[]
    index: number
    itemId?: string
  } | null>(null)
  const [activityFor, setActivityFor] = useState<string | null>(null)
  const [profile, setProfile] = useState<StoryActivityPerson | null>(null)
  const [profileUsed, setProfileUsed] = useState(false)
  const target = useQuery(api.stories.linkTarget, linkItemId ? { itemId: linkItemId } : 'skip')

  useEffect(() => {
    if (!linkItemId || target === undefined || !people) return
    onLinkHandled?.()
    const index = people.findIndex((p) => p.isMe)
    if (target === 'live' && people[index]?.items.some((it) => it.id === linkItemId))
      setOpen({ people, index, itemId: linkItemId })
    else if (target) setActivityFor(linkItemId)
  }, [linkItemId, target, people, onLinkHandled])

  const openProfile = (p: StoryActivityPerson) => {
    setOpen(null)
    setActivityFor(null)
    setProfile(p)
    setProfileUsed(true)
  }

  if (!people || !session) return null
  const mine = people.find((p) => p.isMe)
  return (
    <section aria-label={t('stories.title')} className="-mx-4 mb-5">
      <ul className="flex gap-1 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {!mine && (
          <li className="relative">
            <button
              type="button"
              onClick={() => setComposing(true)}
              aria-label={t('stories.add')}
              className="group flex w-[72px] shrink-0 cursor-pointer flex-col items-center gap-1.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
            >
              <span className="grid place-items-center rounded-full p-[4.5px]">
                <UserAvatar
                  image={session.user.image}
                  name={session.user.name}
                  size={60}
                  className="block rounded-full bg-muted object-cover"
                />
              </span>
              <span className="w-full truncate text-center text-[11.5px] leading-tight text-muted-foreground">
                {t('stories.yourStory')}
              </span>
            </button>
            <AddBadge onClick={() => setComposing(true)} label={t('stories.add')} />
          </li>
        )}
        {people.map((p, i) => (
          <li key={p.userId} className="relative">
            <StoryRing person={p} size={60} onOpen={() => setOpen({ people, index: i })} />
            {p.isMe && <AddBadge onClick={() => setComposing(true)} label={t('stories.add')} />}
          </li>
        ))}
      </ul>
      <ComposerDialog open={composing} onOpenChange={setComposing} initialMode="story" />
      {open && (
        <StoryViewer
          people={open.people}
          start={open.index}
          startItemId={open.itemId}
          onClose={() => setOpen(null)}
          onPerson={openProfile}
        />
      )}
      {activityFor && (
        <StoryActivitySheet
          itemId={activityFor as Id<'storyItems'>}
          onClose={() => setActivityFor(null)}
          onPerson={openProfile}
        />
      )}
      {profileUsed && (
        <Suspense fallback={null}>
          <LeaderboardUserSheet
            userId={profile?.userId ?? null}
            name={profile?.name ?? ''}
            image={profile?.image ?? null}
            onClose={() => setProfile(null)}
          />
        </Suspense>
      )}
    </section>
  )
}

function GoalBadge({ item }: { item: StoryItem }) {
  return (
    <div
      className="flex max-w-full items-center gap-2 self-start rounded-full bg-black/55 py-1.5 pr-3 pl-2 text-white backdrop-blur-md"
      style={{ '--habit': habitColor(item.goalCategory ?? undefined) } as CSSProperties}
    >
      <Target aria-hidden className="size-4 shrink-0 text-[var(--habit)]" strokeWidth={2.5} />
      <span className="min-w-0 truncate text-[13px] font-bold">{item.goalTitle}</span>
    </div>
  )
}

function StatusPanel({ item }: { item: StoryItem }) {
  const { t } = useTranslation()
  if (item.status === 'live') return null
  const [title, hint] =
    item.status === 'pending'
      ? [t('stories.checking'), t('stories.checkingHint')]
      : item.status === 'rejected'
        ? [t('stories.rejected'), t('stories.rejectedHint')]
        : [t('stories.hidden'), t('stories.hiddenHint')]
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center p-8">
      <div
        role="status"
        className="flex max-w-xs flex-col items-center gap-2 rounded-2xl bg-black/65 px-5 py-4 text-center backdrop-blur-md"
      >
        {item.status === 'pending' ? (
          <Loader2 className="size-6 animate-spin" />
        ) : (
          <ShieldAlert className="size-6 text-amber-300" />
        )}
        <p className="text-[15px] font-bold">{title}</p>
        <p className="text-[13px] leading-snug text-white/75">{hint}</p>
      </div>
    </div>
  )
}

function HabitBadge({ item }: { item: StoryItem }) {
  const { t } = useTranslation()
  if (item.goalTitle) return <GoalBadge item={item} />
  if (!item.habitTitle) return null
  return (
    <div
      className="flex max-w-full items-center gap-2 self-start rounded-full bg-black/55 py-1.5 pr-3 pl-2 text-white backdrop-blur-md"
      style={{ '--habit': habitColor(item.habitCategory ?? undefined) } as CSSProperties}
    >
      <span
        aria-hidden
        className="size-3.5 shrink-0 rounded-full bg-[var(--habit)] ring-2 ring-white/70"
      />
      <span className="min-w-0 truncate text-[13px] font-bold">{item.habitTitle}</span>
      {item.streak > 0 && (
        <span
          className="shrink-0 text-[12.5px] font-semibold text-white/85"
          aria-label={t('stories.streak', { days: item.streak })}
        >
          🔥 {item.streak}
        </span>
      )}
    </div>
  )
}

/**
 * Full-screen stories: tap right/left for next/previous (crossing people), hold
 * to pause, Esc or ✕ to close. Photos run 5s; a video runs its length (≤15s).
 */
export function StoryViewer({
  people: initial,
  start,
  startItemId,
  onClose,
  onPerson,
}: {
  people: StoryPerson[]
  start: number
  startItemId?: string
  onClose: () => void
  onPerson?: (p: StoryActivityPerson) => void
}) {
  const { t, i18n } = useTranslation()
  const [people, setPeople] = useState(initial)
  const [pi, setPi] = useState(start)
  const [ii, setIi] = useState(() => {
    const items = initial[start]?.items ?? []
    const idx = startItemId
      ? items.findIndex((it) => it.id === startItemId)
      : items.findIndex((it) => !it.seen)
    return idx < 0 ? 0 : idx
  })
  const [replying, setReplying] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [reacted, setReacted] = useState<Record<string, StoryReaction | null>>({})
  const reactTo = useMutation(api.stories.react)
  const [held, setHeld] = useState(false)
  const [menu, setMenu] = useState<false | 'main' | 'report' | 'block'>(false)
  const [progress, setProgress] = useState(0)
  const [muted, setMuted] = useState(false)
  const [soundBlocked, setSoundBlocked] = useState(false)
  const video = useRef<HTMLVideoElement>(null)
  const markSeen = useMutation(api.stories.markSeen)
  const remove = useMutation(api.stories.remove)
  const report = useMutation(api.stories.report)
  const block = useMutation(api.blocks.block)
  const marked = useRef(new Set<string>())
  const press = useRef<{ at: number; x: number } | null>(null)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const person = people[pi]
  const item = person?.items[ii]
  const paused = held || !!menu || replying || activityOpen

  const go = useCallback(
    (dir: 1 | -1) => {
      setProgress(0)
      setMenu(false)
      if (!person) return
      const next = ii + dir
      if (next >= 0 && next < person.items.length) return setIi(next)
      const np = pi + dir
      if (np < 0) return setIi(0)
      if (np >= people.length) return onClose()
      setPi(np)
      setIi(dir === 1 ? 0 : people[np].items.length - 1)
    },
    [ii, pi, people, person, onClose],
  )

  useEffect(() => {
    if (!item || person.isMe || item.seen || marked.current.has(item.id)) return
    marked.current.add(item.id)
    void markSeen({ itemId: item.id as Id<'storyItems'> }).catch(() => {})
  }, [item, person, markSeen])

  const isVideo = item?.kind === 'video' && !!item.videoUrl
  useEffect(() => {
    if (!item || isVideo || paused) return
    let last = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const dt = now - last
      last = now
      setProgress((p) => {
        const n = p + dt / PHOTO_MS
        return n >= 1 ? 1 : n
      })
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [item, isVideo, paused])

  useEffect(() => {
    if (progress >= 1) go(1)
  }, [progress, go])

  useEffect(() => {
    const v = video.current
    if (!v || item?.kind !== 'video') return
    if (paused) return void v.pause()
    v.play().catch(() => {
      // Autoplay with sound blocked: play muted and offer a tap to unmute.
      if (v.muted) return
      v.muted = true
      setMuted(true)
      setSoundBlocked(true)
      void v.play().catch(() => {})
    })
  }, [item, paused])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (activityOpen) return
      if (e.target instanceof HTMLElement && e.target.closest('input, textarea')) return
      if (e.key === 'Escape') {
        e.preventDefault()
        if (menu) setMenu(false)
        else onClose()
      } else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === ' ') {
        e.preventDefault()
        setHeld((h) => !h)
      }
    }
    window.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [go, menu, onClose, activityOpen])

  if (!person || !item) return null

  const onDown = (e: React.PointerEvent) => {
    press.current = { at: performance.now(), x: e.clientX }
    holdTimer.current = setTimeout(() => setHeld(true), HOLD_MS)
  }
  const onUp = (e: React.PointerEvent) => {
    if (holdTimer.current) clearTimeout(holdTimer.current)
    const p = press.current
    press.current = null
    setHeld(false)
    if (!p || performance.now() - p.at >= HOLD_MS) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    go(p.x - rect.left < rect.width / 3 ? -1 : 1)
  }

  const dropPerson = () => {
    const rest = people.filter((_, i) => i !== pi)
    if (pi >= rest.length) return onClose()
    setPeople(rest)
    setIi(0)
    setProgress(0)
  }

  const dropItem = (id: string) => {
    setMenu(false)
    const items = person.items.filter((it) => it.id !== id)
    if (!items.length) {
      const rest = people.filter((_, i) => i !== pi)
      if (pi >= rest.length) return onClose()
      setPeople(rest)
      setIi(0)
    } else {
      setPeople(people.map((p, i) => (i === pi ? { ...p, items } : p)))
      setIi(Math.min(ii, items.length - 1))
    }
    setProgress(0)
  }

  const deleteItem = () => {
    void remove({ itemId: item.id as Id<'storyItems'> })
      .then(() => toast.success(t('stories.deleted')))
      .catch(() => toast.error(t('stories.deleteFailed')))
    dropItem(item.id)
  }

  const reportItem = (reason: (typeof STORY_REPORT_REASONS)[number]) => {
    void report({ itemId: item.id as Id<'storyItems'>, reason })
      .then(() => toast.success(t('stories.reported')))
      .catch(() => toast.error(t('stories.reportFailed')))
    dropItem(item.id)
  }

  const blockPerson = () => {
    const who = person.name
    setMenu(false)
    void block({ userId: person.userId })
      .then(() => toast.success(t('stories.blocked', { name: who })))
      .catch(() => toast.error(t('stories.blockFailed')))
    dropPerson()
  }

  const mine = item.id in reacted ? reacted[item.id] : item.reaction
  const react = (r: StoryReaction) => {
    const itemId = item.id
    const before = mine
    setReacted((m) => ({ ...m, [itemId]: before === r ? null : r }))
    void reactTo({ itemId: itemId as Id<'storyItems'>, reaction: r }).catch(() => {
      setReacted((m) => ({ ...m, [itemId]: before }))
      toast.error(t('stories.replyFailed'))
    })
  }

  const name = person.isMe ? t('stories.yourStory') : person.name
  const label = item.caption ?? item.habitTitle ?? item.goalTitle ?? name

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('stories.viewer', { name })}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black text-white select-none"
    >
      <div className="relative h-full w-full max-w-[min(100vw,calc(100dvh*9/16))] sm:h-[calc(100%-2rem)]">
        <div className="relative flex size-full flex-col overflow-hidden bg-neutral-900 sm:rounded-2xl">
          <div
            className="absolute inset-0 touch-none"
            onPointerDown={onDown}
            onPointerUp={onUp}
            onPointerCancel={() => {
              if (holdTimer.current) clearTimeout(holdTimer.current)
              setHeld(false)
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            {isVideo ? (
              <video
                key={item.id}
                ref={video}
                src={item.videoUrl ?? undefined}
                poster={item.url ?? undefined}
                aria-label={label}
                playsInline
                muted={muted}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget
                  const total = Math.min(v.duration * 1000 || VIDEO_MAX_MS, VIDEO_MAX_MS)
                  setProgress(Math.min(1, (v.currentTime * 1000) / total))
                }}
                onEnded={() => go(1)}
                className="size-full object-cover"
              />
            ) : item.url ? (
              <img
                key={item.id}
                src={item.url}
                alt={label}
                draggable={false}
                className="size-full object-cover"
              />
            ) : null}
            <StatusPanel item={item} />
          </div>

          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/60 to-transparent" />

          <div className="absolute inset-x-0 top-0 flex flex-col gap-3 px-3 pt-[max(env(safe-area-inset-top),12px)]">
            <div className="flex gap-1" aria-hidden>
              {person.items.map((it, i) => (
                <span
                  key={it.id}
                  className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/35"
                >
                  <span
                    className="block h-full rounded-full bg-white"
                    style={{ width: `${(i < ii ? 1 : i === ii ? progress : 0) * 100}%` }}
                  />
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2.5">
              <UserAvatar
                image={person.image}
                name={person.name}
                size={32}
                className="size-8 rounded-full bg-white/20 object-cover"
              />
              <span className="min-w-0 truncate text-sm font-bold">{name}</span>
              <span className="shrink-0 text-[12.5px] text-white/70">
                {storyAgo(item.createdAt, i18n.language)}
              </span>
              <span className="ml-auto flex items-center gap-1">
                <span className="relative">
                  <button
                    type="button"
                    aria-label={t('stories.more')}
                    aria-expanded={!!menu}
                    onClick={() => setMenu((m) => (m ? false : 'main'))}
                    className="grid size-9 cursor-pointer place-items-center rounded-full hover:bg-white/15"
                  >
                    <MoreHorizontal className="size-5" />
                  </button>
                  {menu && (
                    <div className="absolute top-10 right-0 z-10 w-60 overflow-hidden rounded-xl bg-neutral-800 py-1 shadow-xl ring-1 ring-white/10">
                      {person.isMe ? (
                        <MenuButton danger autoFocus onClick={deleteItem}>
                          <Trash2 className="size-4" />
                          {t('stories.delete')}
                        </MenuButton>
                      ) : menu === 'report' ? (
                        <>
                          <p className="px-3 pt-2 pb-1 text-xs font-semibold text-white/60">
                            {t('stories.reportTitle')}
                          </p>
                          {STORY_REPORT_REASONS.map((r, i) => (
                            <MenuButton key={r} autoFocus={i === 0} onClick={() => reportItem(r)}>
                              {t(`stories.reasons.${r}`)}
                            </MenuButton>
                          ))}
                        </>
                      ) : menu === 'block' ? (
                        <>
                          <p className="px-3 pt-2 pb-1 text-[13px] leading-snug text-white/75">
                            {t('stories.blockConfirm', { name: person.name })}
                          </p>
                          <MenuButton danger autoFocus onClick={blockPerson}>
                            <Ban className="size-4" />
                            {t('stories.block', { name: person.name })}
                          </MenuButton>
                        </>
                      ) : (
                        <>
                          <MenuButton autoFocus onClick={() => setMenu('report')}>
                            <Flag className="size-4" />
                            {t('stories.report')}
                          </MenuButton>
                          <MenuButton danger onClick={() => setMenu('block')}>
                            <Ban className="size-4" />
                            <span className="truncate">
                              {t('stories.block', { name: person.name })}
                            </span>
                          </MenuButton>
                        </>
                      )}
                    </div>
                  )}
                </span>
                <button
                  type="button"
                  aria-label={t('stories.close')}
                  onClick={onClose}
                  className="grid size-9 cursor-pointer place-items-center rounded-full hover:bg-white/15"
                >
                  <X className="size-5" />
                </button>
              </span>
            </div>
          </div>

          {isVideo && (muted || soundBlocked) && (
            <button
              type="button"
              onClick={() => {
                const next = !muted
                setMuted(next)
                setSoundBlocked(false)
                if (video.current) video.current.muted = next
              }}
              className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center gap-2 rounded-full bg-black/55 px-4 py-2 text-sm font-semibold backdrop-blur-md"
            >
              {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
              {muted ? t('stories.unmute') : t('stories.mute')}
            </button>
          )}

          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 px-4 pb-[max(env(safe-area-inset-bottom),20px)]">
            {item.caption && (
              <p className="text-[15px] leading-snug font-semibold drop-shadow">{item.caption}</p>
            )}
            <HabitBadge item={item} />
            {person.isMe
              ? item.status !== 'rejected' && (
                  <SeenByPill
                    itemId={item.id as Id<'storyItems'>}
                    onOpen={() => setActivityOpen(true)}
                  />
                )
              : item.status === 'live' && (
                  <ReplyBar
                    key={item.id}
                    itemId={item.id as Id<'storyItems'>}
                    reaction={mine}
                    onReact={react}
                    onFocusChange={setReplying}
                  />
                )}
          </div>
        </div>
        {activityOpen && (
          <StoryActivitySheet
            itemId={item.id as Id<'storyItems'>}
            onClose={() => setActivityOpen(false)}
            onPerson={onPerson}
          />
        )}

        <button
          type="button"
          aria-label={t('stories.previous')}
          onClick={() => go(-1)}
          className="absolute top-1/2 -left-14 hidden size-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white/15 hover:bg-white/25 sm:grid"
        >
          <ChevronLeft className="size-5" />
        </button>
        <button
          type="button"
          aria-label={t('stories.next')}
          onClick={() => go(1)}
          className="absolute top-1/2 -right-14 hidden size-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white/15 hover:bg-white/25 sm:grid"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>
    </div>,
    document.body,
  )
}

function MenuButton({
  danger,
  autoFocus,
  onClick,
  children,
}: {
  danger?: boolean
  autoFocus?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      autoFocus={autoFocus}
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold hover:bg-white/10',
        danger ? 'text-red-400' : 'text-white',
      )}
    >
      {children}
    </button>
  )
}

const BURST_MS = 750

function BurstBit({ emoji, angle, r }: { emoji: string; angle: number; r: number }) {
  const el = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    el.current?.animate(
      [
        { transform: 'translate(-50%, -50%) scale(0.5)', opacity: 1 },
        {
          transform: `translate(calc(-50% + ${Math.cos(angle) * r}px), calc(-50% + ${Math.sin(angle) * r - 18}px)) scale(1.15)`,
          opacity: 0,
        },
      ],
      { duration: BURST_MS, easing: 'cubic-bezier(.15,.8,.3,1)', fill: 'forwards' },
    )
  }, [angle, r])
  return (
    <span ref={el} className="absolute top-1/2 left-1/2 text-xl">
      {emoji}
    </span>
  )
}

/** A ring of the tapped emoji flying out of its button. */
function Burst({ emoji }: { emoji: string }) {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      {Array.from({ length: 7 }, (_, i) => (
        <BurstBit
          key={i}
          emoji={emoji}
          angle={(i / 7) * Math.PI * 2 - Math.PI / 2}
          r={34 + (i % 2) * 14}
        />
      ))}
    </span>
  )
}

function ReplyBar({
  itemId,
  reaction,
  onReact,
  onFocusChange,
}: {
  itemId: Id<'storyItems'>
  reaction: StoryReaction | null
  onReact: (r: StoryReaction) => void
  onFocusChange: (focused: boolean) => void
}) {
  const { t } = useTranslation()
  const send = useMutation(api.stories.reply)
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [bursts, setBursts] = useState<{ id: number; r: StoryReaction }[]>([])
  const burstId = useRef(0)

  useEffect(() => () => onFocusChange(false), [onFocusChange])

  const tap = (r: StoryReaction) => {
    if (reaction !== r) {
      const id = ++burstId.current
      setBursts((b) => [...b, { id, r }])
      setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), BURST_MS + 50)
    }
    onReact(r)
  }

  const submit = () => {
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    void send({ itemId, text: body })
      .then((res) => {
        if (!res.ok)
          return void toast.error(
            t(res.code === 'rateLimited' ? 'stories.replyLimited' : 'stories.replyFailed'),
          )
        setText('')
        setSent(true)
        input.current?.blur()
        setTimeout(() => setSent(false), 1800)
      })
      .catch(() => toast.error(t('stories.replyFailed')))
      .finally(() => setSending(false))
  }

  return (
    <div className="flex flex-col gap-2.5 pt-1">
      <div role="group" aria-label={t('stories.reactions')} className="flex justify-between px-1">
        {STORY_REACTIONS.map(([r, emoji]) => (
          <button
            key={r}
            type="button"
            aria-label={t('stories.react', { emoji })}
            aria-pressed={reaction === r}
            onClick={() => tap(r)}
            className={cn(
              'relative grid size-11 cursor-pointer place-items-center rounded-full text-[26px] transition-transform hover:scale-110 active:scale-90',
              reaction === r && 'scale-110 bg-white/20 ring-2 ring-white/70',
            )}
          >
            <span className="leading-none">{emoji}</span>
            {bursts
              .filter((b) => b.r === r)
              .map((b) => (
                <Burst key={b.id} emoji={emoji} />
              ))}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="flex items-center gap-2"
      >
        <input
          ref={input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => onFocusChange(true)}
          onBlur={() => onFocusChange(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              e.currentTarget.blur()
            }
          }}
          maxLength={STORY_REPLY_MAX}
          placeholder={t('stories.replyPlaceholder')}
          aria-label={t('stories.replyPlaceholder')}
          className="h-11 min-w-0 flex-1 rounded-full border border-white/45 bg-black/25 px-4 text-[15px] text-white backdrop-blur-md outline-none placeholder:text-white/70 focus:border-white"
        />
        {sent ? (
          <span
            role="status"
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-3.5 text-sm font-semibold backdrop-blur-md"
          >
            <Check className="size-4" strokeWidth={3} />
            {t('stories.replySent')}
          </span>
        ) : (
          text.trim() && (
            <button
              type="submit"
              disabled={sending}
              aria-label={t('stories.send')}
              className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-full bg-white text-black transition-transform active:scale-95 disabled:opacity-60"
            >
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            </button>
          )
        )}
      </form>
    </div>
  )
}

function SeenByPill({ itemId, onOpen }: { itemId: Id<'storyItems'>; onOpen: () => void }) {
  const { t } = useTranslation()
  const activity = useQuery(api.stories.activity, { itemId })
  if (!activity) return null
  const n = activity.viewers.length
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex cursor-pointer items-center gap-2 self-start rounded-full bg-black/55 py-1.5 pr-3.5 pl-2 text-[13px] font-semibold backdrop-blur-md hover:bg-black/70"
    >
      {n > 0 ? (
        <span className="flex -space-x-2">
          {activity.viewers.slice(0, 3).map((v) => (
            <UserAvatar
              key={v.userId}
              image={v.image}
              name={v.name}
              size={22}
              className="size-[22px] rounded-full bg-white/20 object-cover ring-2 ring-black/60"
            />
          ))}
        </span>
      ) : (
        <Eye className="ml-1 size-4" />
      )}
      {t('stories.seenBy', { count: n })}
      {activity.replies.length > 0 && (
        <span className="flex items-center gap-1 text-white/85">
          <MessageCircle className="size-3.5" />
          {activity.replies.length}
        </span>
      )}
    </button>
  )
}

/** Owner only: viewers (with reactions) and private replies. Tap a person for their profile. */
export function StoryActivitySheet({
  itemId,
  onClose,
  onPerson,
}: {
  itemId: Id<'storyItems'>
  onClose: () => void
  onPerson?: (p: StoryActivityPerson) => void
}) {
  const { t, i18n } = useTranslation()
  const activity = useQuery(api.stories.activity, { itemId })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  useEffect(() => {
    if (activity === null) onClose()
  }, [activity, onClose])

  const row = (p: StoryActivityPerson, at: number, trailing: React.ReactNode, text?: string) => (
    <button
      type="button"
      onClick={() => onPerson?.(p)}
      className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-white/8"
    >
      <UserAvatar
        image={p.image}
        name={p.name}
        size={40}
        className="size-10 shrink-0 rounded-full bg-white/15 object-cover"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[15px] font-semibold">{p.name}</span>
          <span className="shrink-0 text-xs text-white/55">{storyAgo(at, i18n.language)}</span>
        </span>
        {text && (
          <span className="text-[14px] leading-snug break-words whitespace-pre-wrap text-white/85">
            {text}
          </span>
        )}
      </span>
      {trailing}
    </button>
  )

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('stories.activity')}
        className="flex max-h-[72dvh] w-full max-w-[min(100vw,calc(100dvh*9/16))] flex-col overflow-hidden rounded-t-2xl bg-neutral-900 text-white shadow-2xl ring-1 ring-white/10"
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
          <h2 className="text-base font-bold">{t('stories.activity')}</h2>
          {activity && (
            <span className="text-sm text-white/60">
              {t('stories.seenBy', { count: activity.viewers.length })}
            </span>
          )}
          <button
            type="button"
            autoFocus
            aria-label={t('stories.close')}
            onClick={onClose}
            className="ml-auto grid size-9 cursor-pointer place-items-center rounded-full hover:bg-white/15"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-2 pt-2 pb-[max(env(safe-area-inset-bottom),12px)]">
          {!activity ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="size-5 animate-spin text-white/60" />
            </div>
          ) : (
            <>
              <h3 className="px-2 pt-1 pb-1 text-xs font-semibold tracking-wide text-white/55 uppercase">
                {t('stories.viewers')}
              </h3>
              {activity.viewers.length ? (
                <ul>
                  {activity.viewers.map((v) => (
                    <li key={v.userId}>
                      {row(
                        v,
                        v.at,
                        v.reaction && (
                          <span
                            className="shrink-0 text-2xl"
                            aria-label={t('stories.react', {
                              emoji: storyReactionEmoji(v.reaction),
                            })}
                          >
                            {storyReactionEmoji(v.reaction)}
                          </span>
                        ),
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-2 py-4 text-sm text-white/60">{t('stories.noViewers')}</p>
              )}
              {activity.replies.length > 0 && (
                <>
                  <h3 className="px-2 pt-4 pb-1 text-xs font-semibold tracking-wide text-white/55 uppercase">
                    {t('stories.replies')}
                  </h3>
                  <ul>
                    {activity.replies.map((r) => (
                      <li key={r.id}>{row(r, r.createdAt, null, r.text)}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
